import { log } from './logger';
import { RecipeTemplateDto } from '@/api/ApiClient';
import { RowType, Source, RecipeState } from '@/components/recipe/types';
import { Lookup } from '@/lib/constants/lookup';
import { IsCompounding, IsCVT, IsFloatingZone } from '@/lib/utils/bulkTechniqueFilters';

/**
 * Returns true if the template expects recipe data from file upload.
 */
export function isRecipeFileUploadTemplate(template: RecipeTemplateDto | null | undefined): boolean {
  const sources = template?.sources ?? legacySourcesFromUsingUpload(template?.usingUpload);
  return sources.includes('Upload');
}

// FIXME: Custom or older recipe JSON may still use usingUpload instead of sources[].
function legacySourcesFromUsingUpload(usingUpload: string | undefined): string[] {
  if (!usingUpload || usingUpload === 'Manual') {
    return [];
  }
  if (usingUpload === 'Upload' || usingUpload === 'Fetch' || usingUpload === 'Pipeline') {
    return [usingUpload];
  }
  return [];
}

/**
 * Returns true if the recipe has no user-entered data (all editable values empty or default).
 * Skips Constant columns - they are template-defined. Used to decide whether to show the recipe file upload area.
 */
export function isRecipeDataEmpty(state: RecipeState | null | undefined): boolean {
  if (!state?.groups) return true;
  for (const group of state.groups) {
    for (const row of group.rows) {
      const values = row.values;
      if (!(values instanceof Map)) continue;
      for (const col of group.columns) {
        if (col.sourceType === Source.Constant) continue;
        const val = values.get(col.symbol);
        const def = col.defaultValue ?? '';
        const empty = val === undefined || val === null || val === '' || String(val) === String(def);
        if (!empty) return false;
      }
    }
  }
  return true;
}

/**
 * Validates if a recipe template is compatible with the given number of startingMaterials.
 *
 * Rules:
 * a.) Only one startingMaterial: Can use any template that explicitly refers that one startingMaterial,
 *     or that refers none (can clean an already grown substrate, or load it into another machine).
 *     Template is optional for single startingMaterial.
 * b.) More than one startingMaterial: Template is required and must reference all startingMaterials.
 *     Either via flexible handling (RowType.StartingMaterials group that generates rows dynamically),
 *     or all startingMaterial indices must be explicitly referenced in the template.
 *     Cannot use a template that doesn't reference startingMaterials at all.
 *     Cannot use a template that doesn't reference all startingMaterials.
 *
 * @param template The recipe template to validate (can be null for optional templates)
 * @param startingMaterialCount The number of startingMaterials
 * @returns true if the template is valid for the given startingMaterial count, false otherwise
 */
export function isValidRecipeTemplateForStartingMaterials(
  template: RecipeTemplateDto | null,
  startingMaterialCount: number
): boolean {
  if (!template) {
    // For 1 startingMaterial, template is optional (can work without recipe)
    // For 2+ startingMaterials, template is required
    return startingMaterialCount === 1;
  }

  if (!template.groups || template.groups.length === 0) {
    // Template has no groups - it's broken anyway and shouldn't be here
    return false;
  }

  // Check for groups with RowType.StartingMaterials - these can handle any number of startingMaterials dynamically
  const hasStartingMaterialsGroup = template.groups.some(
    (group) => group.rowType === RowType.StartingMaterials || group.rowType === 'PR'
  );

  if (hasStartingMaterialsGroup) {
    // Template can handle any number of startingMaterials dynamically - valid for all cases
    return true;
  }

  // Check for columns with fixed startingMaterialIndex references
  // Collect all referenced startingMaterial indices
  const referencedIndices = new Set<number>();

  template.groups.forEach((group) => {
    group.columns?.forEach((col) => {
      if (col.sourceType === Source.StartingMaterial || col.sourceType === 'P') {
        // Check if column has a fixed startingMaterialIndex
        const startingMaterialIndex = (col as any).startingMaterialIndex;
        if (startingMaterialIndex && typeof startingMaterialIndex === 'number') {
          referencedIndices.add(startingMaterialIndex);
        }
      }
    });
  });

  // For single startingMaterial: template must not require more than 1 startingMaterial
  // Template can have no startingMaterial references, or reference startingMaterial 1 only
  if (startingMaterialCount === 1) {
    // If no references, Math.max returns -Infinity which is <= 1, so valid
    const maxReferencedIndex = Math.max(...Array.from(referencedIndices));
    return maxReferencedIndex <= 1;
  }

  // For 2+ startingMaterials: template MUST reference all startingMaterials
  // If no startingMaterial references found, invalid (can't use template that doesn't reference startingMaterials)
  // If there are more, we need to specify how we turn those into one.
  if (referencedIndices.size === 0) {
    return false;
  }

  // Template has fixed startingMaterial references - validate all are referenced
  // Check that template doesn't require more startingMaterials than we have
  const maxReferencedIndex = Math.max(...Array.from(referencedIndices));
  if (maxReferencedIndex > startingMaterialCount) {
    // Template requires more startingMaterials than we have - invalid
    return false;
  }

  // Check that all startingMaterials are referenced (indices 1 through startingMaterialCount)
  for (let i = 1; i <= startingMaterialCount; i++) {
    if (!referencedIndices.has(i)) {
      // Template doesn't reference all startingMaterials - invalid
      return false;
    }
  }

  // All startingMaterials are referenced and template doesn't require more - valid
  return true;
}

/**
 * Check if a template requires startingMaterials and validate that we have enough.
 * Returns validation result with optional error message.
 *
 * @param template The recipe template to validate
 * @param startingMaterialCount The number of startingMaterials available
 * @returns Object with valid flag and optional error message
 */
export function checkStartingMaterialRequirements(
  template: RecipeTemplateDto,
  startingMaterialCount: number
): { valid: boolean; errorMessage?: string } {
  if (!template.groups) {
    return { valid: true };
  }

  // Check if template has any StartingMaterial groups or StartingMaterial columns
  const hasStartingMaterialGroups = template.groups.some((group) => group.rowType === 'PR'); // RowType.StartingMaterials
  const hasStartingMaterialColumns = template.groups.some(
    (group) => group.columns?.some((col) => col.sourceType === 'P') // Source.StartingMaterial
  );

  if (!hasStartingMaterialGroups && !hasStartingMaterialColumns) {
    return { valid: true }; // Template doesn't use startingMaterials
  }

  // Template uses startingMaterials - validate we have them
  if (startingMaterialCount === 0) {
    return {
      valid: false,
      errorMessage:
        'This recipe template requires startingMaterial samples. Please add startingMaterials to the sample first.',
    };
  }

  // For fixed startingMaterial references, check if we have enough
  const maxStartingMaterialIndex = template.groups.reduce((max, group) => {
    const columnMax =
      group.columns?.reduce((colMax, col) => {
        const idx = (col as any).startingMaterialIndex;
        return idx && idx > colMax ? idx : colMax;
      }, 0) || 0;
    return columnMax > max ? columnMax : max;
  }, 0);

  if (maxStartingMaterialIndex > startingMaterialCount) {
    return {
      valid: false,
      errorMessage: `This template requires at least ${maxStartingMaterialIndex} startingMaterial(s) but sample has ${startingMaterialCount}`,
    };
  }

  return { valid: true };
}

/**
 * For preparation activities, some templates set {@link RecipeTemplateDto.usesInstrument} to false
 * (no chamber/reactor row). When true, an instrument must be selected before loading the template or submitting.
 */
export function checkPreparationInstrumentRequirement(
  template: RecipeTemplateDto,
  activity: { type?: string; instrument?: string }
): { valid: boolean; errorMessage?: string } {
  if (activity.type !== 'PREP') {
    return { valid: true };
  }
  if (template.usesInstrument !== true) {
    return { valid: true };
  }
  const hasInstrument = !!activity.instrument && String(activity.instrument).trim() !== '';
  if (!hasInstrument) {
    return {
      valid: false,
      errorMessage: 'This preparation recipe template requires an instrument. Select an instrument before continuing.',
    };
  }
  return { valid: true };
}

/**
 * Enrich recipe state with lookup dropdown values from bulk raw materials.
 * Filters materials by technique type based on lookup column type.
 *
 * @param state The recipe state to enrich
 * @param bulkRawMaterials Array of bulk raw materials to use for lookups
 * @returns Enriched recipe state with dropdown values populated
 */
export function enrichRecipeStateWithLookupValues(
  state: RecipeState,
  bulkRawMaterials: Array<{ id?: number; name?: string; techniques?: string[] }> | undefined
): RecipeState {
  // Check if any lookup columns exist
  const hasLookupColumns = state.groups.some((group) => group.columns.some((col) => col.isLookup && col.lookupValues));

  if (!hasLookupColumns) {
    return state;
  }

  if (!bulkRawMaterials || bulkRawMaterials.length === 0) {
    log.warn('[RecipeUtils] Cannot populate lookup values: bulk raw materials not loaded');
    return state;
  }

  // Clone state and enrich dropdown values
  return {
    ...state,
    groups: state.groups.map((group) => ({
      ...group,
      columns: group.columns.map((column) => {
        if (column.isLookup && column.lookupValues) {
          let filteredMaterials: typeof bulkRawMaterials = [];

          switch (column.lookupValues) {
            case Lookup.RawMaterialCompounding:
              filteredMaterials = bulkRawMaterials.filter((m) => m.techniques?.some((tech) => IsCompounding(tech)));
              break;

            case Lookup.RawMaterialTransport:
              filteredMaterials = bulkRawMaterials.filter((m) => m.techniques?.some((tech) => IsCVT(tech)));
              break;

            case Lookup.RawMaterialFloatingZone:
              filteredMaterials = bulkRawMaterials.filter((m) => m.techniques?.some((tech) => IsFloatingZone(tech)));
              break;

            default:
              filteredMaterials = [];
          }

          // Convert to dropdown format (filter out materials without IDs or names)
          // Preserve the full object so formulas can access all fields via lookup()
          const dropdownValues = filteredMaterials
            .filter((material) => material.id !== undefined && material.name)
            .map((material) => ({
              ...material, // Preserve all fields for lookup() formulas
              id: material.id!,
              label: material.name!,
            }));

          // Add "-- Add new --" option
          dropdownValues.unshift({
            id: -1,
            label: ' -- Add new -- ',
          } as any);

          return { ...column, dropdownValues };
        }
        return column;
      }),
    })),
  };
}
